import { createContext, useContext, useMemo, useState } from "react";

/** Which animation clip the gallery highlights and the settings window edits:
 * an element id + the clip's index within that element's `animations` list. */
export type ActiveClip = { elementId: string; index: number } | null;

type ActiveClipCtx = {
  activeClip: ActiveClip;
  setActiveClip: (clip: ActiveClip) => void;
  // whether the timing & settings panel (expanding above the bottom bar) is
  // open. Shared so the sidebar's "Customize" button can open it even though
  // the panel is rendered by CanvasAnimationBar.
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  // whether the floating animation preview window is visible
  previewVisible: boolean;
  setPreviewVisible: (visible: boolean) => void;
};

const Context = createContext<ActiveClipCtx>({
  activeClip: null,
  setActiveClip: () => {},
  settingsOpen: false,
  setSettingsOpen: () => {},
  previewVisible: true,
  setPreviewVisible: () => {},
});

export const ActiveClipProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [activeClip, setActiveClip] = useState<ActiveClip>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(true);
  const value = useMemo(
    () => ({
      activeClip,
      setActiveClip,
      settingsOpen,
      setSettingsOpen,
      previewVisible,
      setPreviewVisible,
    }),
    [activeClip, settingsOpen, previewVisible],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useActiveClip = () => useContext(Context);
